import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, API_BASE, getToken, photoUrl } from '../services/apiClient';
import { jobLabel } from '../utils/ui';

const STATUS_META = {
  approved:                 { label: 'Approved · Holding',  bg: '#dcfce7', color: '#15803d' },
  approved_with_conditions: { label: 'Approved w/ Cond.',   bg: '#fef3c7', color: '#b45309' },
  approved_no_action:       { label: 'Approved · No Action', bg: '#ecfccb', color: '#4d7c0f' },
};

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ApprovedSubmittals() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null); // submittal id whose PDF is generating

  useEffect(() => {
    apiFetch('/approved-submittals')
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  // Download the merged submittal package PDF (auth header, so keep token out of URL).
  async function viewPackage(item) {
    setBusy(item.id);
    // Open the tab synchronously inside the tap handler — iOS Safari blocks a
    // window.open() fired after an await as a non-user-gesture popup.
    const win = window.open('', '_blank');
    try {
      const res = await fetch(`${API_BASE}/sub-jobs/${item.sub_job_id}/submittals/${item.id}/generate-pdf`, {
        headers: { 'x-worker-token': getToken() },
      });
      if (!res.ok) throw new Error('Could not open the package');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (win) win.location = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (win) win.close();
      alert(e.message || 'Failed to open the submittal package');
    } finally {
      setBusy(null);
    }
  }

  const ql = q.trim().toLowerCase();
  const filtered = ql
    ? rows.filter(r => `${r.project_name} ${r.category} ${r.spec_number || ''}`.toLowerCase().includes(ql))
    : rows;

  // Group by project.
  const groups = [];
  const byProj = {};
  filtered.forEach(r => {
    if (!byProj[r.project_id]) { byProj[r.project_id] = { project_id: r.project_id, name: jobLabel({ project_number: r.project_number, name: r.project_name }), items: [] }; groups.push(byProj[r.project_id]); }
    byProj[r.project_id].items.push(r);
  });

  return (
    <div className="page" style={{ maxWidth: 820, margin: '0 auto', padding: '0 0 40px' }}>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 16px 10px', position: 'sticky', top: 0, background: '#0f2747', zIndex: 5 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 22, lineHeight: 1, padding: 4 }}>‹</button>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>Approved Submittals</div>
          <div style={{ fontSize: 12, color: '#9fb3d1' }}>Approved specs &amp; cut sheets across active jobs</div>
        </div>
      </div>

      <div style={{ padding: '12px 14px 0' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search job, category, or spec…"
          style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '10px 12px', fontSize: 14, marginBottom: 12 }} />

        {loading && <div style={{ color: '#6b7280', padding: 20, textAlign: 'center' }}>Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div style={{ color: '#6b7280', padding: 30, textAlign: 'center', background: '#fff', border: '1.5px solid #ececec', borderRadius: 12 }}>
            No approved submittals yet.
          </div>
        )}

        {groups.map(g => (
          <div key={g.project_id} style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#0f2747', padding: '2px 2px 8px' }}>{g.name}</div>
            {g.items.map(item => {
              const sm = STATUS_META[item.status] || STATUS_META.approved;
              return (
                <div key={item.id} style={{ background: '#fff', border: '1.5px solid #ececec', borderRadius: 12, padding: 14, marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                        {String(item.titan_number || '').padStart(2, '0')} — {item.category || 'Submittal'}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        {item.spec_number ? `Spec ${item.spec_number} · ` : ''}Approved {fmtDate(item.updated_at)}
                      </div>
                    </div>
                    <span style={{ background: sm.bg, color: sm.color, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, whiteSpace: 'nowrap' }}>{sm.label}</span>
                  </div>

                  {/* Individual approved documents */}
                  {Array.isArray(item.documents) && item.documents.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 10, paddingTop: 10, borderTop: '1px solid #f1f1f1' }}>
                      {item.documents.map(doc => (
                        <a key={doc.id} href={photoUrl(doc.filename)} target="_blank" rel="noreferrer"
                          style={{ fontSize: 13, color: '#1d4ed8', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span>📎</span><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.original_name}</span>
                        </a>
                      ))}
                    </div>
                  )}

                  <div style={{ marginTop: 12 }}>
                    <button onClick={() => viewPackage(item)} disabled={busy === item.id}
                      style={{ background: '#1e3a5f', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, padding: '8px 14px', borderRadius: 8, cursor: busy === item.id ? 'default' : 'pointer', width: '100%' }}>
                      {busy === item.id ? 'Opening…' : '📄 View Approved Package'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
